import React, { useState, useEffect } from 'react';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { makeStyles } from '@mui/styles';
import { api } from 'common';
import Button from 'components/CustomButtons/Button.js';
import AlertDialog from '../components/AlertDialog';
import CircularLoading from '../components/CircularLoading';
import { FONT_FAMILY, WEB_MAIN_COLOR } from '../common/sharedFunctions';
import { colors } from '../components/Theme/WebTheme';
import SupportMenuEditor from './SupportMenuEditor';
import { buildDefaultSupportMenu } from 'common/src/other/supportMenu';
import { mergeLegalContentDefaults } from 'common/src/other/legalContentDefaults';

const useStyles = makeStyles((theme) => ({
  form: (props) => ({
    width: '100%',
    marginTop: theme.spacing(1),
    backgroundColor: WEB_MAIN_COLOR(props?.settings?.appCat),
    borderRadius: '8px',
  }),
  container1: {
    backgroundColor: colors.Header_Text,
    borderBottomLeftRadius: '8px',
    borderBottomRightRadius: '8px',
    padding: '30px',
    width: '100%',
  },
  title: {
    color: colors.WHITE,
    marginBottom: '15px',
    paddingTop: '15px',
    paddingLeft: '15px',
    fontSize: '20px',
    paddingRight: '15px',
    fontFamily: FONT_FAMILY,
  },
  buttonStyle: (props) => ({
    margin: 0,
    minWidth: 250,
    height: 40,
    borderRadius: '30px',
    backgroundColor: WEB_MAIN_COLOR(props?.settings?.appCat),
    color: colors.WHITE,
    fontWeight: 'bold',
    fontFamily: FONT_FAMILY,
  }),
  textField: (props) => ({
    '& label.Mui-focused': {
      color: WEB_MAIN_COLOR(props?.settings?.appCat),
    },
    '& .MuiOutlinedInput-root.Mui-focused fieldset': {
      borderColor: WEB_MAIN_COLOR(props?.settings?.appCat),
    },
    '& input': { fontFamily: FONT_FAMILY },
    '& textarea': { fontFamily: FONT_FAMILY },
  }),
  rootRtl: {
    '& label': { right: 10, left: 'auto', paddingRight: 20, fontFamily: FONT_FAMILY },
    '& legend': { textAlign: 'right', marginRight: 20, fontFamily: FONT_FAMILY },
    '& input, & textarea': { fontFamily: FONT_FAMILY },
  },
}));

export default function LegalSupportSettings() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const { editSettings, clearSettingsViewError } = api;
  const settingsdata = useSelector((state) => state.settingsdata);
  const dispatch = useDispatch();
  const classes = useStyles({ settings: settingsdata.settings });
  const [data, setData] = useState(null);
  const [clicked, setClicked] = useState(false);

  useEffect(() => {
    if (settingsdata.settings) {
      const merged = mergeLegalContentDefaults(settingsdata.settings, t);
      if (!merged.supportMenuItems?.length) {
        merged.supportMenuItems = buildDefaultSupportMenu(merged);
      }
      setData({ ...merged });
    }
  }, [settingsdata.settings, i18n.language, t]);

  const handleTextChange = (e) => {
    setData({ ...data, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!data.AllowCriticalEditsAdmin) {
      alert(t('demo_mode'));
      return;
    }
    setClicked(true);
    dispatch(editSettings(data));
  };

  const handleClose = () => {
    setClicked(false);
    dispatch(clearSettingsViewError());
  };

  const fieldClass = isRTL === 'rtl' ? classes.rootRtl : classes.textField;

  if (!data) {
    return <CircularLoading />;
  }

  return (
    <form className={classes.form}>
      <Typography component="h1" variant="h5" className={classes.title} style={{ textAlign: isRTL === 'rtl' ? 'right' : 'left' }}>
        {t('legal_support_settings', 'Soporte y contenido legal')}
      </Typography>
      <div className={classes.container1}>
        <Grid container spacing={2} style={{ direction: isRTL === 'rtl' ? 'rtl' : 'ltr' }}>
          <Grid item xs={12}>
            <Alert severity="info" sx={{ fontFamily: FONT_FAMILY, mb: 2 }}>
              {t(
                'legal_support_settings_hint',
                'Edita el texto que ven los usuarios en Soporte, Preguntas frecuentes, Política de privacidad y Autorización de datos. Pulsa ENVIAR para guardar en Firebase y publicar en la app.',
              )}
            </Alert>
          </Grid>

          <Grid item xs={12}>
            <Typography component="h2" variant="h6" style={{ fontFamily: FONT_FAMILY, textAlign: isRTL === 'rtl' ? 'right' : 'left' }}>
              {t('legal_app_content_title', 'Contenido en la app móvil')}
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              className={fieldClass}
              variant="outlined"
              margin="normal"
              fullWidth
              multiline
              minRows={8}
              id="faqContent"
              label={t('faq_content', 'Preguntas frecuentes')}
              name="faqContent"
              onChange={handleTextChange}
              value={data.faqContent || ''}
              InputLabelProps={{ style: { fontFamily: FONT_FAMILY } }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              className={fieldClass}
              variant="outlined"
              margin="normal"
              fullWidth
              multiline
              minRows={8}
              id="dataAuthContent"
              label={t('data_auth_content', 'Autorización tratamiento de datos')}
              name="dataAuthContent"
              onChange={handleTextChange}
              value={data.dataAuthContent || ''}
              InputLabelProps={{ style: { fontFamily: FONT_FAMILY } }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              className={fieldClass}
              variant="outlined"
              margin="normal"
              fullWidth
              multiline
              minRows={10}
              id="privacyContent"
              label={t('privacy_policy', 'Política de tratamiento de información')}
              name="privacyContent"
              onChange={handleTextChange}
              value={data.privacyContent || ''}
              InputLabelProps={{ style: { fontFamily: FONT_FAMILY } }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              className={fieldClass}
              variant="outlined"
              margin="normal"
              fullWidth
              multiline
              minRows={10}
              id="termsContent"
              label={t('term_condition', 'Términos y condiciones')}
              name="termsContent"
              onChange={handleTextChange}
              value={data.termsContent || ''}
              InputLabelProps={{ style: { fontFamily: FONT_FAMILY } }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              className={fieldClass}
              variant="outlined"
              margin="normal"
              fullWidth
              multiline
              minRows={5}
              id="aboutUsContent"
              label={t('about_us', 'Quiénes somos')}
              name="aboutUsContent"
              onChange={handleTextChange}
              value={data.aboutUsContent || ''}
              InputLabelProps={{ style: { fontFamily: FONT_FAMILY } }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              className={fieldClass}
              variant="outlined"
              margin="normal"
              fullWidth
              multiline
              minRows={5}
              id="contactUsContent"
              label={t('contact_us', 'Contáctanos')}
              name="contactUsContent"
              onChange={handleTextChange}
              value={data.contactUsContent || ''}
              InputLabelProps={{ style: { fontFamily: FONT_FAMILY } }}
            />
          </Grid>

          <Grid item xs={12}>
            <SupportMenuEditor data={data} setData={setData} classes={classes} isRTL={isRTL} />
          </Grid>

          <Grid item xs={12}>
            <Button
              size="lg"
              onClick={handleSubmit}
              variant="contained"
              color="secondaryButton"
              className={classes.buttonStyle}
            >
              <Typography style={{ fontFamily: FONT_FAMILY, margin: 5 }}>{t('submit')}</Typography>
            </Button>
          </Grid>
        </Grid>
      </div>
      <AlertDialog open={settingsdata.error.flag && clicked} onClose={handleClose}>
        {t('update_failed')}
      </AlertDialog>
    </form>
  );
}
